import { DELIMITER_OPTS } from 'constants/importConfigurations';
import type {
  AdminImportConfigurationIndexFields,
  AdminImportConfigurationIndexInclude,
  AdminImportConfigurationShowFields,
  AdminImportConfigurationShowInclude,
  Typed,
} from 'services/api/fields';

import { ClassificationOptions } from './Classification';
import { Client, ClientOptions } from './Client';
import { GroupOptions } from './Group';
import { GroupTypeOptions } from './GroupType';

// ---------------------------------------------------------------------------
// Mapping sub-types
// ---------------------------------------------------------------------------

export class ClassificationType {
  apiName: string;
  displayName: string;
  id: string | null;
  name: string;

  constructor(options: Partial<ClassificationType> = {}) {
    this.apiName = options.apiName || '';
    this.displayName = options.displayName || '';
    this.id = options.id || null;
    this.name = options.name || '';
  }
}

export class GroupTypeType {
  apiName: string;
  displayName: string;
  id: string | null;
  name: string;

  constructor(options: Partial<GroupTypeType> = {}) {
    this.apiName = options.apiName || '';
    this.displayName = options.displayName || '';
    this.id = options.id || null;
    this.name = options.name || '';
  }
}

export interface Mapping<MappingType, DefaultValueType> {
  columnName: string;
  type: MappingType | null;
  defaultValue: DefaultValueType | null;
}

// ---------------------------------------------------------------------------
// Config shape
// ---------------------------------------------------------------------------

interface PropertyConfig {
  importKey: string;
  olioDefault: string;
}

export interface ImportConfigurationConfig {
  dateFormat: string;
  delimiter: string | { label: string; value: string };
  properties: Record<string, string | PropertyConfig>;
  lookups: {
    episodes: Record<string, string | string[]>;
    plan_types: Record<string, string | string[]>;
    groups: Record<string, string | string[]>;
    group_types: Record<string, string | string[]>;
  };
  mappings: {
    classifications: Mapping<ClassificationType, ClassificationOptions>[];
    groups: Mapping<GroupTypeOptions, GroupOptions>[];
    groupTypes: Mapping<GroupTypeType, null>[];
    transferredFrom: Mapping<GroupTypeOptions, GroupOptions>;
  };
}

// ---------------------------------------------------------------------------
// Options / defaults
// ---------------------------------------------------------------------------

export interface ImportConfigurationOptions {
  client?: ClientOptions;
  config?: Partial<ImportConfigurationConfig>;
  [key: string]: any;
}

const defaultMappings = {
  classifications: [],
  groups: [],
  groupTypes: [],
  transferredFrom: { columnName: '', type: null, defaultValue: null },
};

const defaultConfig: ImportConfigurationConfig = {
  dateFormat: '%m/%d/%Y',
  delimiter: DELIMITER_OPTS[0].value,
  properties: {},
  lookups: { episodes: {}, plan_types: {}, groups: {}, group_types: {} },
  mappings: defaultMappings,
};

const defaults: ImportConfigurationOptions = {
  id: '',
  clientId: '',
  config: defaultConfig,
  type: '',
  updatedAt: '',
};

// ---------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------

// Forward declare so _ImportConfiguration's constructor can return the infused type
let ImportConfiguration: new <T extends ImportConfigurationOptions>(options: T) => Typed<_ImportConfiguration<T>, T>;

class _ImportConfiguration<T extends ImportConfigurationOptions = ImportConfigurationOptions> {
  client!: Client<NonNullable<T['client']>>;
  config!: ImportConfigurationConfig;

  constructor(options: T) {
    const opts = { ...defaults, ...options };
    Object.assign(this, opts);

    this.client = new Client<NonNullable<T['client']>>(opts.client!);
    this.config = {
      ...defaultConfig,
      ...opts.config,
      lookups: { ...defaultConfig.lookups, ...opts.config?.lookups },
      mappings: {
        ...defaultMappings,
        ...opts.config?.mappings,
        groupTypes: opts.config?.mappings?.groupTypes || [],
      },
    };
  }

  get clientName() {
    return this.client.name;
  }

  get clientType() {
    return this.client.clientType;
  }
}

ImportConfiguration = _ImportConfiguration as typeof ImportConfiguration; // eslint-disable-line prefer-const

export default ImportConfiguration;
export { ImportConfiguration };
export type ImportConfiguration<T extends ImportConfigurationOptions = ImportConfigurationOptions> =
  _ImportConfiguration<T> & Omit<T, keyof _ImportConfiguration<T>>;

// ---------------------------------------------------------------------------
// Typed variants
// ---------------------------------------------------------------------------

export type TypedImportConfiguration<I extends AdminImportConfigurationShowInclude = never> = ImportConfiguration<
  AdminImportConfigurationShowFields<I>
>;

export type TypedImportConfigurationIndex<I extends AdminImportConfigurationIndexInclude = never> = ImportConfiguration<
  AdminImportConfigurationIndexFields<I>
>;
